"use client";

import { CheckCircle2, CircleAlert, Clock3, Link2, Loader2, Save, UserPlus, UserRound } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { Alert, Dialog } from "@nitro/ui";
import { io } from "socket.io-client";
import { API_URL, apiFetch } from "../lib/api";
import { getRegistrationAvailability, getRegistrationPresentation } from "../lib/competitionRegistration";
import styles from "./CompetitionRegistrationModal.module.css";

export function CompetitionRegistrationButton({ className = "nitro-primary-button", competition, guestLabel = "Iniciar sesion / Registro" }) {
  const router = useRouter();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [sessionState, setSessionState] = useState("checking");
  const [registrationState, setRegistrationState] = useState(null);
  const buttonLabel = registrationButtonLabel(competition, registrationState);
  const registrationAvailability = getRegistrationAvailability(competition);
  const registrationPresentation = getRegistrationPresentation(competition);
  const isRegistrationOpen = registrationAvailability.canRegister;
  const registrationHref = registrationEntryHref(pathname, competition);

  useEffect(() => {
    let active = true;
    apiFetch("/api/v1/auth/me", { cache: "no-store" })
      .then(async () => {
        if (!active) return;
        setSessionState("authenticated");
        if (isRegistrationOpen && competition.slug) {
          try {
            const data = await apiFetch(`/api/v1/competitions/${encodeURIComponent(competition.slug)}/registration`, { cache: "no-store" });
            if (active) setRegistrationState(data);
          } catch {
            if (active) setRegistrationState(null);
          }
        }
      })
      .catch(() => {
        if (active) setSessionState("guest");
      });

    return () => {
      active = false;
    };
  }, [competition.slug, isRegistrationOpen]);

  function closeModal() {
    setOpen(false);
    if (shouldRefresh) {
      setShouldRefresh(false);
      router.refresh();
    }
  }

  if (!isRegistrationOpen) {
    return (
      <button
        className={className}
        disabled
        title={registrationAvailability.reason || registrationPresentation.statusLabel}
        type="button"
      >
        <Clock3 size={16} />
        {registrationPresentation.statusLabel || buttonLabel}
      </button>
    );
  }

  if (isRegistrationOpen && sessionState === "guest") {
    return (
      <a className={className} href={registrationHref}>
        <UserPlus size={16} />
        {guestLabel}
      </a>
    );
  }

  return (
    <>
      <button className={className} disabled={sessionState === "checking"} onClick={() => setOpen(true)} type="button">
        {sessionState === "checking" ? <Loader2 size={16} /> : <CheckCircle2 size={16} />}
        {buttonLabel}
      </button>
      {open ? (
        <CompetitionRegistrationModal
          competition={competition}
          onClose={closeModal}
          onRegistered={(data) => {
            if (data?.status) setRegistrationState(data);
            setShouldRefresh(true);
          }}
          registrationHref={registrationHref}
        />
      ) : null}
    </>
  );
}

function CompetitionRegistrationModal({ competition, onClose, onRegistered, registrationHref }) {
  const [state, setState] = useState({ loading: true, error: "", data: null, authRequired: false });
  const [accountValue, setAccountValue] = useState("");
  const [registerForm, setRegisterForm] = useState({ carId: "", carClassId: "", gameAccountId: "", skinId: "", raceNumber: "" });
  const [busy, setBusy] = useState(false);
  const [liveNotice, setLiveNotice] = useState("");

  useEffect(() => {
    loadRegistrationState();
    function onMessage(event) {
      if (event.origin !== window.location.origin && !String(event.origin).includes("nitrosimracing.com.co")) return;
      if (
        event.data?.source === "nitro-v4"
        && ["nitro_iracing_link", "nitro_steam_link", "nitro_social_login"].includes(event.data?.type)
      ) {
        loadRegistrationState();
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [competition.slug]);

  useEffect(() => {
    const socket = io(API_URL, {
      withCredentials: true,
      transports: ["websocket"],
    });

    socket.on("connect", () => {
      socket.emit("competition:join", { competitionSlug: competition.slug });
    });

    socket.on("competition:registration-updated", (payload) => {
      if (payload?.competition?.slug !== competition.slug) return;
      setLiveNotice("Cupos actualizados en tiempo real.");
      setState((current) => {
        if (!current.data) return current;
        const isFull = !!payload.capacity?.isFull;
        return {
          ...current,
          data: {
            ...current.data,
            capacity: payload.capacity || current.data.capacity,
            status: isFull && current.data.status.step === "ready"
              ? {
                  ...current.data.status,
                  canRegister: false,
                  step: "full",
                  reason: "Los cupos estan completos.",
                  cta: "Cupos completos",
                }
              : current.data.status,
          },
        };
      });
      onRegistered?.();
    });

    return () => socket.disconnect();
  }, [competition.slug]);

  const nextRequirement = useMemo(() => {
    return state.data?.eligibility?.requiredAccounts?.find((item) => item.status !== "ready") || null;
  }, [state.data]);

  const selectedCarRequired = (state.data?.availableCars || []).length > 1;

  async function loadRegistrationState(silent = false, selectedGameAccountId = "", selectedCarId = "") {
    if (!silent) {
      setState((current) => ({ ...current, loading: true, error: "", authRequired: false }));
    }
    try {
      const params = new URLSearchParams();
      if (selectedGameAccountId) params.set("gameAccountId", selectedGameAccountId);
      if (selectedCarId) params.set("carId", selectedCarId);
      const suffix = params.toString() ? `?${params.toString()}` : "";
      const data = await apiFetch(`/api/v1/competitions/${encodeURIComponent(competition.slug)}/registration${suffix}`, { cache: "no-store", timeoutMs: 30000 });
      setState({ loading: false, error: "", data, authRequired: false });
      const firstCar = data.availableCars?.length === 1 ? String(data.availableCars[0].id) : "";
      const carClassCategories = (data.availableCategories || []).filter((category) => category.categoryType === "car_class");
      const firstCarClass = carClassCategories.length === 1 ? String(carClassCategories[0].carClassId || carClassCategories[0].id) : "";
      const gameAccounts = registrationGameAccounts(data);
      const selectedAccount = data.driverClassDetection?.selectedAccountId
        ? String(data.driverClassDetection.selectedAccountId)
        : "";
      const firstGameAccount = gameAccounts.length === 1 ? String(gameAccounts[0].id) : "";
      const firstSkin = data.availableSkins?.length === 1 ? String(data.availableSkins[0].id) : "";
      const selectedSkin = firstSkin ? data.availableSkins[0] : null;
      setRegisterForm((current) => ({
        ...current,
        carId: selectedCarId || current.carId || firstCar,
        carClassId: current.carClassId || firstCarClass,
        gameAccountId: selectedGameAccountId || selectedAccount || current.gameAccountId || firstGameAccount,
        skinId: current.skinId || firstSkin,
        raceNumber: data.registrationRequirements?.raceNumber?.mode === "derived_from_skin" && selectedSkin
          ? selectedSkin.raceNumber || ""
          : current.raceNumber,
      }));
    } catch (error) {
      setState({ loading: false, error: error.status === 401 ? "" : error.message, data: null, authRequired: error.status === 401 });
    }
  }

  async function connectAccount(requirement) {
    const platform = requirement?.platform;
    if (!platform) return;
    if (platform.slug === "steam") {
      openConnectorPopup(`${API_URL}/api/v1/pilot-accounts/connect/steam/start`, "nitroSteamConnect");
      return;
    }
    if (platform.slug === "iracing") {
      openConnectorPopup(`${API_URL}/api/v1/iracing/connect/start`, "nitroIracingConnect");
      return;
    }
    if (!accountValue.trim()) {
      setState((current) => ({ ...current, error: `Escribe tu ${platform.valueLabel || platform.name}.` }));
      return;
    }
    setBusy(true);
    try {
      await apiFetch(`/api/v1/pilot-accounts/connect/${encodeURIComponent(platform.slug)}`, {
        method: "POST",
        body: JSON.stringify({
          accountValue: accountValue.trim(),
          displayValue: accountValue.trim(),
        }),
      });
      setAccountValue("");
      await loadRegistrationState();
    } catch (error) {
      setState((current) => ({
        ...current,
        error: error.message,
        data: error.details?.capacity && current.data ? {
          ...current.data,
          capacity: error.details.capacity,
          status: {
            ...current.data.status,
            canRegister: false,
            step: "full",
            reason: "Los cupos estan completos.",
            cta: "Cupos completos",
          },
        } : current.data,
      }));
    } finally {
      setBusy(false);
    }
  }

  async function confirmRegistration() {
    setBusy(true);
    setState((current) => ({ ...current, error: "" }));
    try {
      const data = await apiFetch(`/api/v1/competitions/${encodeURIComponent(competition.slug)}/register`, {
        method: "POST",
        body: JSON.stringify(registerForm),
      });
      setState({ loading: false, error: "", data, authRequired: false });
      onRegistered?.(data);
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }));
    } finally {
      setBusy(false);
    }
  }

  const dialogDescription = [
    competition.typeLabel || "Competencia",
    state.data?.competition?.simulator?.name || competition.game || competition.simulator?.name,
  ].filter(Boolean).join(" · ");

  return (
    <Dialog
      bodyClassName={styles.body}
      className={styles.dialog}
      closeDisabled={busy}
      density="compact"
      description={dialogDescription}
      dismissible={!busy}
      dismissOnBackdrop={false}
      onClose={onClose}
      open
      size="md"
      title={competition.title}
    >
      {state.loading ? (
        <div className="registration-loading"><Loader2 size={22} /> Cargando inscripcion...</div>
      ) : null}

      {!state.loading && state.authRequired ? (
        <AuthRedirectStep registrationHref={registrationHref} />
      ) : null}

      {!state.loading && state.data ? (
        <div className="registration-flow">
          <RegistrationSummary data={state.data} />
          {state.error ? <Alert className={styles.alert} tone="danger">{state.error}</Alert> : null}
          {liveNotice ? <Alert className={styles.alert} tone="warning">{liveNotice}</Alert> : null}
          <RegistrationChecklist data={state.data} />

          {state.data.status.step === "profile" ? (
            <div className="registration-action-panel">
              <CircleAlert size={18} />
              <div>
                <strong>Falta completar tu perfil</strong>
                <p>Campos pendientes: {state.data.profile.missingFields.join(", ")}.</p>
                <a className="nitro-primary-button compact" href="/perfil"><UserRound size={15} /> Completar perfil</a>
              </div>
            </div>
          ) : null}

          {["account", "account_pending"].includes(state.data.status.step) ? (
            <AccountStep
              accountValue={accountValue}
              busy={busy}
              onAccountValue={setAccountValue}
              onConnect={connectAccount}
              requirement={nextRequirement}
            />
          ) : null}

          {state.data.status.step === "ready" ? (
            <ReadyStep
              busy={busy}
              data={state.data}
              form={registerForm}
              onAccountChange={(gameAccountId) => {
                const resetsDetectedNumber = (state.data?.availableCategories || [])
                  .some((category) => category.categoryType === "driver_class");
                setRegisterForm((current) => ({
                  ...current,
                  gameAccountId,
                  raceNumber: resetsDetectedNumber ? "" : current.raceNumber,
                }));
                loadRegistrationState(false, gameAccountId, registerForm.carId);
              }}
              onCarChange={(carId) => {
                setRegisterForm((current) => ({ ...current, carId, carClassId: "", skinId: "", raceNumber: "" }));
                loadRegistrationState(false, registerForm.gameAccountId, carId);
              }}
              onChange={setRegisterForm}
              onConfirm={confirmRegistration}
              selectedCarRequired={selectedCarRequired}
            />
          ) : null}

          {state.data.status.step === "registered" ? (
            <div className="registration-success-panel">
              <CheckCircle2 size={22} />
              <div>
                <strong>{state.data.status.reason}</strong>
                <p>La reglamentacion queda aceptada con tu inscripcion.</p>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}

function AuthRedirectStep({ registrationHref }) {
  return (
    <div className="registration-action-panel">
      <UserPlus size={18} />
      <div>
        <strong>Entra para inscribirte</strong>
        <p>Crea tu piloto o inicia sesion. Al terminar vuelves a este evento.</p>
        <a className="nitro-primary-button compact" href={registrationHref}>
          <UserPlus size={15} />
          Iniciar sesion / Registro
        </a>
      </div>
    </div>
  );
}

function RegistrationSummary({ data }) {
  const detection = data.driverClassDetection;
  return (
    <div className="registration-summary">
      <span>{data.competition.simulator?.name}</span>
      <strong>{detection?.status === "ready" ? `${detection.category.name} / iRating ${Math.trunc(detection.rating)}` : data.capacity.label}</strong>
      <em>{detection?.status === "ready" ? "Categoria detectada segun reglamento" : data.status.reason}</em>
    </div>
  );
}

function RegistrationChecklist({ data }) {
  const checks = [
    { label: "Sesion iniciada", ok: true },
    { label: "Perfil minimo", ok: data.profile.complete },
    { label: "Cuenta del simulador", ok: !!data.eligibility?.canRace },
    { label: data.capacity.max ? "Cupos disponibles" : "Inscripcion abierta", ok: !data.capacity.isFull },
  ];
  return (
    <div className="registration-checklist">
      {checks.map((check) => (
        <span className={check.ok ? "is-ok" : "is-pending"} key={check.label}>
          {check.ok ? <CheckCircle2 size={15} /> : <CircleAlert size={15} />}
          {check.label}
        </span>
      ))}
    </div>
  );
}

function AccountStep({ accountValue, busy, onAccountValue, onConnect, requirement }) {
  const platform = requirement?.platform;
  if (!platform) return null;
  if (requirement.status === "identity_conflict") {
    return (
      <div className="registration-action-panel">
        <CircleAlert size={18} />
        <div>
          <strong>Identidad {platform.name} en conflicto</strong>
          <p>Esta identidad aparece activa para mas de un piloto. Ninguna de esas cuentas puede inscribirse hasta que un administrador resuelva los datos.</p>
        </div>
      </div>
    );
  }
  const oauth = ["oauth", "steam_openid"].includes(platform.connectorType) || ["steam", "iracing"].includes(platform.slug);
  return (
    <div className="registration-action-panel">
      <UserPlus size={18} />
      <div>
        <strong>Conecta {platform.name}</strong>
        <p>{requirement.instructions || platform.valueHelp || "Esta cuenta es necesaria para inscribirte en este simulador."}</p>
        {!oauth ? (
          <input
            placeholder={platform.valuePlaceholder || platform.valueLabel || platform.name}
            value={accountValue}
            onChange={(event) => onAccountValue(event.target.value)}
          />
        ) : null}
        <button className="nitro-primary-button compact" disabled={busy} onClick={() => onConnect(requirement)} type="button">
          {oauth ? <Link2 size={15} /> : <Save size={15} />}
          {oauth ? `Conectar con ${platform.name}` : "Guardar cuenta"}
        </button>
      </div>
    </div>
  );
}

function ReadyStep({ busy, data, form, onAccountChange, onCarChange, onChange, onConfirm, selectedCarRequired }) {
  const carClassCategories = (data.availableCategories || []).filter((category) => category.categoryType === "car_class");
  const driverClassCategories = (data.availableCategories || []).filter((category) => category.categoryType === "driver_class");
  const gameAccounts = registrationGameAccounts(data);
  const requirements = data.registrationRequirements || {};
  const shouldSelectCar = requirements.car?.mode === "required";
  const shouldSelectCarClass = requirements.category?.mode === "required";
  const shouldSelectSkin = requirements.skin?.mode !== "disabled";
  const skinRequired = requirements.skin?.mode === "required";
  const raceNumberMode = requirements.raceNumber?.mode || "optional";
  const raceNumberRequired = ["required", "derived_from_skin"].includes(raceNumberMode);
  const accountPlatformName = data.eligibility?.requiredAccounts?.find((item) => item.status === "ready")?.platform?.name || "simulador";
  const detection = data.driverClassDetection;
  const detectedCategory = detection?.status === "ready" ? detection.category : null;
  const availableNumbers = Array.isArray(detection?.availableNumbers) ? detection.availableNumbers : [];
  const canSubmit = !busy
    && (!gameAccounts.length || !!form.gameAccountId)
    && (!selectedCarRequired || !!form.carId)
    && (!shouldSelectCarClass || !!form.carClassId)
    && (!skinRequired || !!form.skinId)
    && (!raceNumberRequired || !!String(form.raceNumber || "").trim())
    && (!driverClassCategories.length || (detection?.status === "ready" && !!String(form.raceNumber || "").trim()));

  return (
    <div className="registration-ready-step">
      {gameAccounts.length > 1 ? (
        <label>
          Cuenta {accountPlatformName}
          <select required value={form.gameAccountId} onChange={(event) => onAccountChange(event.target.value)}>
            <option value="">Selecciona cuenta</option>
            {gameAccounts.map((account, index) => (
              <option key={account.id} value={account.id}>
                {account.displayValue || `Cuenta ${accountPlatformName} ${index + 1}`}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {shouldSelectCar ? (
        <label>
          Auto
          <select required={selectedCarRequired} value={form.carId} onChange={(event) => onCarChange(event.target.value)}>
            <option value="">Selecciona auto</option>
            {data.availableCars.map((car) => <option key={car.id} value={car.id}>{car.name}</option>)}
          </select>
        </label>
      ) : null}
      {shouldSelectCarClass ? (
        <label>
          Categoria de auto
          <select required value={form.carClassId} onChange={(event) => onChange({ ...form, carClassId: event.target.value })}>
            <option value="">Selecciona categoria</option>
            {carClassCategories.map((category) => (
              <option key={category.id} value={category.carClassId || category.id}>{category.name}</option>
            ))}
          </select>
        </label>
      ) : null}
      {shouldSelectSkin ? (
        <label>
          Skin
          <select
            disabled={!form.carId || !(data.availableSkins || []).length}
            required={skinRequired}
            value={form.skinId}
            onChange={(event) => {
              const skinId = event.target.value;
              const skin = (data.availableSkins || []).find((item) => String(item.id) === skinId);
              onChange({
                ...form,
                skinId,
                raceNumber: raceNumberMode === "derived_from_skin" ? skin?.raceNumber || "" : form.raceNumber,
              });
            }}
          >
            <option value="">{(data.availableSkins || []).length ? "Selecciona skin" : "Selecciona primero un auto"}</option>
            {(data.availableSkins || []).map((skin) => (
              <option key={skin.id} value={skin.id}>
                {skin.name}{skin.raceNumberValid ? ` · #${skin.raceNumber}` : ""}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {driverClassCategories.length ? (
        <div className="registration-detected-card">
          <span>Categoria detectada</span>
          {detectedCategory ? (
            <>
              <strong>{detectedCategory.name}</strong>
              <p>iRating usado: {Math.trunc(detection.rating)}. Regla: {categoryRuleText(detectedCategory)}.</p>
            </>
          ) : (
            <>
              <strong>Pendiente</strong>
              <p>{detection?.message || "Necesitamos el historial de iRating para asignar tu categoria."}</p>
            </>
          )}
        </div>
      ) : null}
      {raceNumberMode !== "disabled" ? <label>
        Numero de piloto
        {driverClassCategories.length ? (
          <select disabled={!detectedCategory || !availableNumbers.length} required value={form.raceNumber} onChange={(event) => onChange({ ...form, raceNumber: event.target.value })}>
            <option value="">{availableNumbers.length ? "Selecciona numero disponible" : "Sin numeros disponibles"}</option>
            {availableNumbers.map((number) => <option key={number} value={number}>{number}</option>)}
          </select>
        ) : raceNumberMode === "derived_from_skin" ? (
          <input disabled placeholder="Se deriva de la skin" value={form.raceNumber} />
        ) : (
          <input
            inputMode="numeric"
            maxLength={3}
            placeholder={raceNumberMode === "required" ? "Obligatorio" : "Opcional"}
            required={raceNumberMode === "required"}
            value={form.raceNumber}
            onChange={(event) => onChange({ ...form, raceNumber: event.target.value.replace(/\D/g, "").slice(0, 3) })}
          />
        )}
      </label> : null}
      <p className="registration-terms">Al confirmar aceptas el reglamento, condiciones e instrucciones de la competencia.</p>
      <button className="nitro-primary-button" disabled={!canSubmit} onClick={onConfirm} type="button">
        {busy ? <Loader2 size={16} /> : <CheckCircle2 size={16} />}
        {data.status.cta}
      </button>
    </div>
  );
}

function registrationGameAccounts(data) {
  return (data.eligibility?.requiredAccounts || [])
    .flatMap((requirement) => requirement.accounts || [])
    .filter((account) => account.verificationStatus === "verified" && account.status === "active" && !account.identityConflict);
}

function categoryRuleText(category) {
  const min = category?.minRating;
  const max = category?.maxRating;
  const numberMin = category?.numberMin;
  const numberMax = category?.numberMax;
  const ratingRule = min ? `iRating >= ${min}` : max ? `iRating <= ${max}` : "sin rango iRating";
  const numberRule = numberMin && numberMax ? `numeros ${numberMin}-${numberMax}` : "sin rango de numero";
  return `${ratingRule}, ${numberRule}`;
}

function registrationButtonLabel(competition, registrationState = null) {
  const step = registrationState?.status?.step;
  if (step === "registered") {
    return registrationState?.existingEntry?.status === "pending" ? "Inscripcion pendiente" : "Inscrito";
  }
  const registration = getRegistrationPresentation(competition);
  return registration.ctaLabel || competition.ctaLabel || "Ver evento";
}

function registrationEntryHref(pathname, competition) {
  const returnTo = pathname || `/eventos/${competition.slug || ""}`;
  const params = new URLSearchParams({
    return: returnTo,
    intent: "inscripcion",
  });

  if (competition?.slug) {
    params.set("competition", competition.slug);
  }

  return `/registro?${params.toString()}`;
}

function openConnectorPopup(url, name) {
  window.open(url, name, "width=720,height=780,noopener=false,noreferrer=false");
}
